import useButtonActivateHandler from "@jnctn/hooks/useButtonActivateHandler";
import FlexLayoutItem, {
  layouts,
} from "@jnctn/design-system/Flexbox/FlexLayoutItem.component";
import Text from "@jnctn/design-system/Text/Text.component";
import React, {
  cloneElement,
  ComponentProps,
  useCallback,
  useContext,
} from "react";
import { useMatch, useNavigate } from "react-router";
import cx from "classnames";
import { ColorContext, Color } from "./Menu.component";
import { ErrorContext } from "@jnctn/error-handling/ErrorBoundary.component";
import match, { Matcher } from "@jnctn/fp/match";

const iconColor = match<Color, Matcher<boolean, string>>(
  ["black", match([false, "text-charcoal-500"], [true, "text-white"])],
  ["white", match([false, "text-charcoal-400"], [true, "text-charcoal-800"])]
);

const textColor = match<Color, ComponentProps<typeof Text>["color"]>(
  ["black", "white"],
  ["white", "primary"]
);

export default function MenuItem({
  icon,
  iconActive,
  label,
  to,
  iconWrapperClassName,
  alwaysActive = false,
}: {
  icon: React.ReactElement<{ className?: string }>;
  iconActive?: React.ReactElement<{ className?: string }>;
  label: string;
  to: string;
  iconWrapperClassName?: string;
  alwaysActive?: boolean;
}) {
  const { reset } = useContext(ErrorContext);
  const color = useContext(ColorContext);
  const active = !!useMatch(to);
  const navigate = useNavigate();
  const onNavigate = useCallback(() => {
    reset();
    navigate(to);
  }, [to, reset, navigate]);
  const onKeyPress = useButtonActivateHandler<HTMLDivElement>(onNavigate);

  return (
    <FlexLayoutItem
      key={to}
      className="h-20 text-center cursor-pointer select-none"
      shrink={0}
      basis={0}
      grow={1}
      layout={layouts.verticalCenterAll}
      onClick={onNavigate}
      tabIndex={1}
      onKeyPress={onKeyPress}
    >
      <FlexLayoutItem
        className={cx(
          iconWrapperClassName,
          "transition-colors",
          !active && alwaysActive
            ? "text-charcoal-600"
            : iconColor(color)(active)
        )}
      >
        {cloneElement(active && iconActive !== undefined ? iconActive : icon, {
          className: cx("w-10 h-10", icon.props.className),
        })}
      </FlexLayoutItem>
      <FlexLayoutItem>
        <Text
          color={textColor(color)}
          size="body3"
          weight={active ? "bold" : "semibold"}
          className="transition-colors"
        >
          {label}
        </Text>
      </FlexLayoutItem>
    </FlexLayoutItem>
  );
}
